import {MUIContainer} from '@/components/MUI-Components';
import DisplayImage from '@/components/Ui-components/display-image';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import {memo} from 'react';
import {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import {contentMaxWidth, colors, borderRadius, spacing, typography, fontSize, fontWeight, transition} from '@/styles/tokens';
import React from 'react';

export interface PlatformPreviewItem {
    id: number;
    imageSrc: StaticImageData;
    alt: string;
    imageLeft: boolean;
    youtubeUrl?: string;
    title: string;
    subtitle: string;
    description: string;
    bullets?: string[];
    tags?: string[];
    ctaLabel: string;
    ctaHref: string;
}

interface PlatformPreviewsProps {
    heading: React.ReactNode;
    subheading: string;
    platforms: PlatformPreviewItem[];
}

const sectionSx = {
    py: {xs: 6, md: 8}
} as const;

const headingSx = {
    ...typography.sectionHeading,
    color: colors.text.primary,
    textAlign: 'center',
    mb: 2
} as const;

const subtitleSx = {
    ...typography.body,
    color: colors.text.primary,
    textAlign: 'center',
    maxWidth: contentMaxWidth.narrow,
    mx: 'auto',
    mb: 8
} as const;

const imageOuterSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
} as const;

const imageInnerSx = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
} as const;

const cardTitleSx = {
    ...typography.cardTitle,
    color: colors.text.primary,
    mb: 1.5
} as const;

const cardSubtitleSx = {
    fontWeight: fontWeight.bold,
    fontSize: typography.body.fontSize,
    color: colors.text.primary,
    lineHeight: typography.body.lineHeight,
    mb: 1.5
} as const;

const cardDescSx = {
    ...typography.body,
    color: colors.text.primary,
    mb: 2.5
} as const;

const bulletsListSx = {
    listStyle: 'none',
    p: 0,
    m: 0,
    mb: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[12]
} as const;

const bulletItemSx = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[12],
    ...typography.body,
    color: colors.text.primary,
    '&::before': {
        content: '"■"',
        color: colors.primary.deep,
        fontSize: '9px',
        flexShrink: 0
    }
} as const;

const tagsWrapperSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[8],
    mb: 2.5
} as const;

const tagSx = {
    bgcolor: colors.background.tag,
    color: colors.primary.deep,
    ...typography.caption,
    borderRadius: borderRadius.full,
    px: spacing[16],
    py: spacing[8],
    lineHeight: typography.caption.lineHeight
} as const;

const ctaSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing[8],
    bgcolor: colors.primary.deep,
    color: colors.text.inverse,
    fontSize: fontSize[14],
    fontWeight: fontWeight.medium,
    px: spacing[16],
    py: spacing[12],
    borderRadius: borderRadius.lg,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: transition.base,
    '&:hover': {bgcolor: colors.primary.main, color: colors.text.inverse}
} as const;

function getYoutubeId(url: string): string | null {
    const match = url.match(/(?:youtu\.be\/|[?&]v=)([\w-]+)/);
    return match ? match[1] : null;
}

const PlatformPreviews = ({heading, subheading, platforms}: PlatformPreviewsProps) => {
    return (
        <Box sx={sectionSx} className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
            <MUIContainer>
                <Typography variant="h2" component="h2" className="font-headers" sx={headingSx}>
                    {heading}
                </Typography>
                <Typography className="font-regular-text" sx={subtitleSx}>
                    {parse(subheading)}
                </Typography>

                <Box display="flex" flexDirection="column" gap={4}>
                    {platforms.map((platform) => (
                        <Grid
                            key={platform.id}
                            container
                            spacing={{xs: 4, md: 5}}
                            alignItems="center"
                            direction={{xs: 'column', md: platform.imageLeft ? 'row' : 'row-reverse'}}
                        >
                            <Grid item xs={12} md={6.5}>
                                <Box sx={imageOuterSx}>
                                    {platform.youtubeUrl && getYoutubeId(platform.youtubeUrl) ? (
                                        <Box sx={{...imageInnerSx, p: 0, overflow: 'hidden', width: '100%'}}>
                                            <iframe
                                                width="100%"
                                                height="490"
                                                src={`https://www.youtube-nocookie.com/embed/${getYoutubeId(platform.youtubeUrl)}?rel=0`}
                                                title={platform.alt}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                loading="lazy"
                                                style={{border: 'none', display: 'block', width: '100%', borderRadius: borderRadius['2xl']}}
                                            />
                                        </Box>
                                    ) : (
                                        <Box sx={imageInnerSx}>
                                            <DisplayImage
                                                src={platform.imageSrc}
                                                alt={platform.alt}
                                                width={520}
                                                height={400}
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    borderRadius: borderRadius['10'],
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={5.5} display="flex" alignItems="center">
                                <Box>
                                    <Typography component="h3" className="font-headers" sx={cardTitleSx}>
                                        {platform.title}
                                    </Typography>
                                    <Typography className="font-headers" sx={cardSubtitleSx}>
                                        {platform.subtitle}
                                    </Typography>
                                    <Typography className="font-regular-text remove-underline" sx={cardDescSx}>
                                        {parse(platform.description)}
                                    </Typography>
                                    {platform.bullets && platform.bullets.length > 0 && (
                                        <Box component="ul" sx={bulletsListSx}>
                                            {platform.bullets.map((bullet, i) => (
                                                <Box key={i} component="li" className="font-regular-text" sx={bulletItemSx}>
                                                    {bullet}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                    <Box sx={tagsWrapperSx}>
                                        {platform.tags &&
                                            platform.tags.map((tag) => (
                                                <Box key={tag} component="span" className="font-regular-text" sx={tagSx}>
                                                    {tag}
                                                </Box>
                                            ))}
                                    </Box>
                                    {platform.ctaLabel && (
                                        <Link href={platform.ctaHref} passHref legacyBehavior>
                                            <Box component="a" className="font-regular-text" sx={ctaSx}>
                                                {platform.ctaLabel} ↗
                                            </Box>
                                        </Link>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    ))}
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(PlatformPreviews);
